// ---------------------------------------------------------------
// Alarm Report — the SOP requires an alarm report 4x/day:
//   6:30 AM, 12:30 PM, 6:30 PM, 12:30 AM (night shift sends the 6:30 AM first)
// Subject: "ALARM REPORT (MM/DD/YYYY)"
// To: Ronen Koubi, Carlos Vega, Nadim Téllez   CC: Inbound
// Body: greeting + table of alarms (Trailer #) and Resolved (Yes/No).
// Follow-up reports go as replies in the same thread.
// This module compiles the window's alarms and drafts the email.
// ---------------------------------------------------------------

import { getAlertsBetween } from './alertLog';

// scheduled report times, in minutes-from-midnight
const REPORT_TIMES = [30, 750, 1110, 390].sort((a, b) => a - b); // 00:30, 06:30, 12:30, 18:30
// note: SOP lists 6:30 AM (390), 12:30 PM (750), 6:30 PM (1110), 12:30 AM (30)

const RECIP_KEY = 'dd-alarm-recipients';
// Names below are template placeholders (example customer). Every field is
// editable per customer, and each recipient's "contact" can be an email OR a
// phone number — email contacts get the report by mail, phone contacts by SMS
// (once an SMS connector is wired at deployment).
const DEFAULT_RECIP = {
  to: [{ name: 'Ronen Koubi', contact: '' }, { name: 'Carlos Vega', contact: '' }, { name: 'Nadim Téllez', contact: '' }],
  cc: [{ name: 'Inbound', contact: '' }],
};

// A contact string is an email if it has an @, otherwise treated as a phone.
export const isEmail = (c) => !!c && /@/.test(c);
export const isPhone = (c) => !!c && !isEmail(c) && /[0-9]/.test(c);

export function getRecipients() {
  try {
    const saved = JSON.parse(localStorage.getItem(RECIP_KEY));
    if (!saved) return DEFAULT_RECIP;
    // migrate any old {email} shape → {contact}
    const fix = (arr) => (arr || []).map((r) => ({ name: r.name || '', contact: r.contact || r.email || '' }));
    return { to: fix(saved.to), cc: fix(saved.cc) };
  } catch { return DEFAULT_RECIP; }
}
export function saveRecipients(r) { localStorage.setItem(RECIP_KEY, JSON.stringify(r)); }

const t12 = (min) => {
  const h = Math.floor(min / 60), m = min % 60;
  const ap = h < 12 ? 'AM' : 'PM';
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
};

// Given a moment, find the current report slot and the previous one → the window.
export function reportWindow(now = new Date()) {
  const mins = now.getHours() * 60 + now.getMinutes();
  // current slot = latest report time <= now (else previous day's last slot)
  let curIdx = -1;
  REPORT_TIMES.forEach((rt, i) => { if (mins >= rt) curIdx = i; });
  const start = new Date(now), end = new Date(now);
  if (curIdx === -1) {
    // before first slot today → window is yesterday's last slot to now
    const last = REPORT_TIMES[REPORT_TIMES.length - 1];
    start.setDate(start.getDate() - 1); start.setHours(Math.floor(last / 60), last % 60, 0, 0);
    end.setHours(Math.floor(REPORT_TIMES[0] / 60), REPORT_TIMES[0] % 60, 0, 0);
  } else {
    const prevIdx = curIdx - 1;
    const cur = REPORT_TIMES[curIdx];
    if (prevIdx < 0) {
      const last = REPORT_TIMES[REPORT_TIMES.length - 1];
      start.setDate(start.getDate() - 1); start.setHours(Math.floor(last / 60), last % 60, 0, 0);
    } else {
      const prev = REPORT_TIMES[prevIdx];
      start.setHours(Math.floor(prev / 60), prev % 60, 0, 0);
    }
    end.setHours(Math.floor(cur / 60), cur % 60, 0, 0);
    if (end < now) end.setTime(now.getTime()); // include right up to now
  }
  return { start, end, label: `${t12(start.getHours() * 60 + start.getMinutes())} and ${t12(end.getHours() * 60 + end.getMinutes())}` };
}

// Build the report object: subject, recipients, rows, plain-text + html body.
export function buildAlarmReport(now = new Date()) {
  const { start, end, label } = reportWindow(now);
  const recip = getRecipients();
  const alerts = getAlertsBetween(start.toISOString(), end.toISOString());

  // rows: one per alarm. "Trailer #" = the entity that alarmed; Resolved defaults No.
  const rows = alerts.map((a) => ({ alarm: a.entity || a.title || '—', title: a.title || '', resolved: 'No' }));

  const dateStr = now.toLocaleDateString('en-US');
  const subject = `ALARM REPORT ${dateStr}`;

  const textTable = rows.length
    ? rows.map((r) => `  • ${r.alarm} — ${r.title} — Resolved: ${r.resolved}`).join('\n')
    : '  • No alarms in this window.';
  const body = `Good Day,\n\nPlease see below the alarm between ${label}:\n\n${textTable}\n\nThank you.`;

  const rowsHtml = rows.length
    ? rows.map((r) => `<tr><td>${r.alarm}</td><td>${r.resolved}</td></tr>`).join('')
    : '<tr><td colspan="2" style="color:#64748b">No alarms in this window</td></tr>';
  const html = `<div style="font-family:Segoe UI,Arial,sans-serif;color:#0f172a">
    <p>Good Day,</p>
    <p>Please see below the alarm between <b>${label}</b>:</p>
    <table style="border-collapse:collapse" border="1" cellpadding="8">
      <thead><tr style="background:#e8eef7"><th>ALARM (TRAILER #)</th><th>RESOLVED (YES/NO)</th></tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <p>Thank you.</p>
  </div>`;

  return { subject, to: recip.to, cc: recip.cc, rows, label, start, end, body, html };
}

// Draft the email in the default mail client (auto-send once an email connector
// is wired at deployment; today this pre-fills a ready-to-send message).
// Only email-type contacts go into the mailto; phone contacts are delivered by
// SMS once that connector is wired.
export function openAlarmReportEmail(now = new Date()) {
  const r = buildAlarmReport(now);
  const to = r.to.map((x) => x.contact).filter(isEmail).join(',');
  const cc = r.cc.map((x) => x.contact).filter(isEmail).join(',');
  const url = `mailto:${to}?cc=${cc}&subject=${encodeURIComponent(r.subject)}&body=${encodeURIComponent(r.body)}`;
  window.open(url, '_blank');
  return r;
}
